/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { MinusCircleFilled, PlusCircleFilled } from '@ant-design/icons'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import React, { useState } from 'react'
import WarningModal from 'src/components/WarningModal'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import { useSpareConfirmState } from '../../hooks/useSpareConfirmState'
import type { AttendancePlayer } from '../../types'
import {
  DEMOTE_TOOLTIP,
  PROMOTE_TOOLTIP,
  WITHDRAW_TOOLTIP,
} from '../../utils/statusCopy'
import { useAttendanceSection } from './AttendanceSectionContext'

interface PromoteActionsProps {
  player: AttendancePlayer
}

const PromoteActions: React.FC<PromoteActionsProps> = ({ player }) => {
  const { onWithdrawSpare } = useAttendanceSection()
  const { confirmed, canPromote, canWithdraw, inFlight, onToggleConfirm } =
    useSpareConfirmState(player)
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false)
  const availabilityRequest = player.availability_request

  const onPressWithdraw = () => {
    if (!canWithdraw || inFlight || !onWithdrawSpare || !availabilityRequest) {
      return
    }
    setWithdrawConfirmOpen(true)
  }

  const onConfirmWithdraw = () => {
    setWithdrawConfirmOpen(false)
    if (onWithdrawSpare && availabilityRequest) {
      onWithdrawSpare(availabilityRequest.id)
    }
  }

  // Already promoted: the minus reverses the promotion rather than withdrawing
  // the request.
  const minusTooltip = confirmed ? DEMOTE_TOOLTIP : WITHDRAW_TOOLTIP

  return (
    <div css={actionsStyle}>
      {canPromote ? (
        <Tooltip title={PROMOTE_TOOLTIP} zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}>
          <PlusCircleFilled
            css={actionIconStyle(inFlight)}
            style={{ color: colors.PRIMARY }}
            aria-label={PROMOTE_TOOLTIP}
            onClick={inFlight ? undefined : onToggleConfirm}
          />
        </Tooltip>
      ) : (
        // Placeholder keeps the minus icon in the same column across rows that
        // only render one action.
        <span css={actionIconPlaceholderStyle} aria-hidden />
      )}
      {canWithdraw || confirmed ? (
        <Tooltip title={minusTooltip} zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}>
          <MinusCircleFilled
            css={actionIconStyle(inFlight)}
            style={{ color: colors.ERROR_300 }}
            aria-label={minusTooltip}
            onClick={
              inFlight
                ? undefined
                : confirmed
                ? onToggleConfirm
                : onPressWithdraw
            }
          />
        </Tooltip>
      ) : (
        <span css={actionIconPlaceholderStyle} aria-hidden />
      )}
      <WarningModal
        isOpen={withdrawConfirmOpen}
        onClose={() => setWithdrawConfirmOpen(false)}
        onConfirm={onConfirmWithdraw}
        title='Withdraw Request'
        description='Are you sure you want to withdraw this availability request? The player will no longer appear as a spare candidate for this game.'
        confirmLabel='WITHDRAW'
        cancelLabel='CANCEL'
        destructive
      />
    </div>
  )
}

/** PROMOTE column (Eligible Game Spares): promote + withdraw / demote. */
const PromoteCell = ({ data }: ICellRendererParams<AttendancePlayer>) =>
  data ? <PromoteActions player={data} /> : null

export default PromoteCell

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`
const actionIconStyle = (disabled: boolean) => css`
  font-size: 24px;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: ${disabled ? 0.5 : 1};
`
/* Same footprint as an action icon so a one-action row stays column-aligned
   with a two-action row. */
const actionIconPlaceholderStyle = css`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`
